import React, { useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

/* ─── Icono y color por tipo de archivo ─── */
const FILE_TYPE_CONFIG = {
  image: { icon: "mdi:file-image-outline", color: "#f59e0b" },
  pdf: { icon: "mdi:file-pdf-box", color: "#ef4444" },
  word: { icon: "mdi:file-word-outline", color: "#3b82f6" },
  excel: { icon: "mdi:file-excel-outline", color: "#22c55e" },
  ppt: { icon: "mdi:file-powerpoint-outline", color: "#f97316" },
  video: { icon: "mdi:file-video-outline", color: "#8b5cf6" },
  audio: { icon: "mdi:file-music-outline", color: "#ec4899" },
  zip: { icon: "mdi:folder-zip-outline", color: "#64748b" },
  default: { icon: "mdi:file-document-outline", color: "#64748b" },
};

const getFileType = (fileName) => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (!ext) return FILE_TYPE_CONFIG.default;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext)) return FILE_TYPE_CONFIG.image;
  if (ext === "pdf") return FILE_TYPE_CONFIG.pdf;
  if (["doc", "docx"].includes(ext)) return FILE_TYPE_CONFIG.word;
  if (["xls", "xlsx", "csv"].includes(ext)) return FILE_TYPE_CONFIG.excel;
  if (["ppt", "pptx"].includes(ext)) return FILE_TYPE_CONFIG.ppt;
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return FILE_TYPE_CONFIG.video;
  if (["mp3", "wav", "ogg", "flac", "aac", "m4a"].includes(ext)) return FILE_TYPE_CONFIG.audio;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return FILE_TYPE_CONFIG.zip;
  return FILE_TYPE_CONFIG.default;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 KB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EntregaModal = ({
  show,
  onHide,
  onSubmit,
  submitting = false,
  actividad,
}) => {
  const [comentario, setComentario] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (show) {
      setComentario("");
      setFiles([]);
    }
  }, [show]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    files.forEach((file) => formData.append("archivos", file));
    if (comentario.trim()) formData.append("comentario_alumno", comentario.trim());
    onSubmit(formData);
  };

  if (!show) return null;

  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onHide(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">

          {/* Header */}
          <div className="modal-header py-20 px-24 border-0"
            style={{ background: "linear-gradient(135deg, #eff6ff, #dbeafe)", borderBottom: "3px solid #3b82f630" }}>
            <div className="d-flex align-items-center gap-12">
              <div className="w-40-px h-40-px bg-primary-50 d-flex align-items-center justify-content-center radius-10">
                <Icon icon="mdi:cloud-upload-outline" className="text-primary-600" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h5 className="modal-title fw-semibold mb-2">Entregar actividad</h5>
                <span className="text-secondary-light text-sm">{actividad?.titulo}</span>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onHide} disabled={submitting} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body px-24 py-20">
              {/* Fecha límite */}
              {actividad?.fecha_entrega && (
                <div className="d-flex align-items-center gap-8 mb-16 p-12 radius-10"
                  style={{ backgroundColor: "#fefce8", border: "1px solid #fde68a" }}>
                  <Icon icon="mdi:calendar-alert" style={{ color: "#d97706", fontSize: "20px", flexShrink: 0 }} />
                  <span className="text-sm">
                    Fecha límite: <strong style={{ color: "#92400e" }}>
                      {new Date(actividad.fecha_entrega).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </strong>
                  </span>
                </div>
              )}

              {/* Upload */}
              <div className="mb-20">
                <label className="form-label fw-medium text-secondary-light mb-8">Archivos adjuntos</label>
                <div
                  className={`classroom-file-upload-area ${files.length > 0 ? "has-files" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("classroom-drag-over"); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("classroom-drag-over")}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("classroom-drag-over");
                    setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
                  }}
                >
                  <Icon icon={files.length > 0 ? "mdi:file-check-outline" : "mdi:cloud-upload-outline"}
                    style={{ fontSize: "44px", color: files.length > 0 ? "#22c55e" : "#94a3b8" }} />
                  <p className="mt-12 mb-4 fw-medium text-sm">
                    {files.length > 0 ? `${files.length} archivo(s) seleccionado(s)` : "Arrastra archivos o haz clic para seleccionar"}
                  </p>
                  <p className="text-secondary-light text-xs mb-0">Imágenes, PDF, Word, Excel, PPTX, video, audio, ZIP</p>
                  <input ref={fileInputRef} type="file" multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.ogg,.flac,.aac,.m4a,.zip,.rar,.7z,.tar,.gz"
                    onChange={handleFileChange} style={{ display: "none" }} />
                </div>

                {files.length > 0 && (
                  <div className="d-flex flex-column gap-8 mt-12">
                    {files.map((file, idx) => {
                      const typeCfg = getFileType(file.name);
                      return (
                        <div key={idx} className="classroom-file-item d-flex align-items-center justify-content-between"
                          style={{ padding: "10px 14px", borderLeft: `3px solid ${typeCfg.color}` }}>
                          <div className="d-flex align-items-center gap-10 min-w-0">
                            <Icon icon={typeCfg.icon} style={{ color: typeCfg.color, fontSize: "22px", flexShrink: 0 }} />
                            <div className="min-w-0">
                              <span className="text-sm fw-medium d-block text-truncate">{file.name}</span>
                              <span className="text-secondary-light text-xs">{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                          <button type="button" className="btn p-0 d-flex align-items-center text-danger flex-shrink-0 ms-8"
                            onClick={() => removeFile(idx)} title="Quitar">
                            <Icon icon="mdi:close-circle" style={{ fontSize: "20px" }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Comentario */}
              <div className="mb-12">
                <label className="form-label fw-medium text-secondary-light mb-8">
                  Comentario para el mentor <span className="text-secondary-light fw-normal ms-4">(opcional)</span>
                </label>
                <textarea className="form-control radius-10"
                  placeholder="Agrega una nota, explicación o aclaración sobre tu entrega..."
                  value={comentario} onChange={(e) => setComentario(e.target.value)}
                  rows={3} style={{ resize: "vertical" }} />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer py-16 px-24 border-0 d-flex justify-content-between align-items-center">
              <small className="text-secondary-light">
                {files.length > 0 ? `${files.length} archivo(s) • ${formatFileSize(totalSize)} total` : "Sin archivos adjuntos"}
              </small>
              <div className="d-flex gap-8">
                <button type="button" className="btn btn-outline-secondary radius-10 px-16" onClick={onHide} disabled={submitting}>Cancelar</button>
                <button type="submit" className="btn btn-primary d-flex align-items-center gap-8 radius-10 px-20" disabled={submitting}>
                  {submitting ? (<><span className="spinner-border spinner-border-sm" /><span>Enviando...</span></>)
                    : (<><Icon icon="mdi:send-outline" style={{ fontSize: "18px" }} /><span>Entregar</span></>)}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EntregaModal;
